import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib';
import type { UnderlinedTabsProps } from '@/types';
import { useEffect, useRef, useState, type FC } from 'react';

interface ExtendedUnderlinedTabsProps extends UnderlinedTabsProps {
  makeAbsolute?: boolean;
}

const UnderlinedTabs: FC<ExtendedUnderlinedTabsProps> = ({
  tabItems,
  makeSticky,
  makeAbsolute = false,
  subTab = false,
  value,
  onValueChange,
  className,
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    if (!makeSticky || !sentinelRef.current) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsStuck(!entry.isIntersecting);
    });

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [makeSticky]);

  return (
    <>
      {makeSticky ? <div ref={sentinelRef} className="h-px" /> : null}

      <Tabs
        defaultValue={tabItems[0].value}
        value={value}
        onValueChange={onValueChange}
        className={cn(className, makeSticky && !subTab ? 'sticky top-0 z-50' : '')}
      >
        <TabsList
          className={
            subTab
              ? `
                relative
                ${makeAbsolute ? 'min-[1015px]:absolute min-[1015px]:right-0 min-[1015px]:-top-3' : ''}
                inline-flex
                w-full sm:w-fit
                bg-muted/40
                border
                gap-1
                px-1
                overflow-x-auto
                whitespace-nowrap
                max-w-full
                [-ms-overflow-style:none]
                [scrollbar-width:none]
                [&::-webkit-scrollbar]:hidden
              `
              : `
                block
                w-full
                p-0
                bg-transparent
                border-b
                rounded-none
                mb-3
                space-x-6
                overflow-x-auto
                whitespace-nowrap
                max-w-full
                [-ms-overflow-style:none]
                [scrollbar-width:none]
                [&::-webkit-scrollbar]:hidden
                ${makeSticky
                ? `sticky top-0 ${isStuck ? 'bg-background h-17 z-50' : 'bg-transparent'
                }`
                : ''
              }
              `
          }
        >
          {tabItems.map((item) => (
            <TabsTrigger
              value={item.value}
              key={item.value}
              className={
                subTab
                  ? 'px-3 py-1 text-sm h-full data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground text-muted-foreground border-none data-[state=active]:border-border'
                  : 'rounded-none h-full data-[state=active]:shadow-none data-[state=active]:bg-transparent dark:data-[state=active]:bg-transparent border-0 border-b-2 border-transparent data-[state=active]:border-primary dark:data-[state=active]:border-primary'
              }
            >
              <span className="flex items-center gap-2">
                {item.icon && <span>{item.icon}</span>}
                {item.title}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {tabItems.map((item) => (
          <TabsContent value={item.value} key={item.value}>
            {item.content}
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
};

export default UnderlinedTabs;
